import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
// performNuclearLogout 已不再從 WS 這條路呼叫（見下方 onclose 說明：是否登出統一交給
// HTTP 401 攔截器判定，避免兩個獨立的登出觸發點造成使用者莫名被登出），故不再 import。
import { API_BASE_URL, refreshAccessToken } from '../services/api';

const WebSocketContext = createContext(null);

// 重連退避：從 3 秒起、每次加倍、上限 30 秒；連上後歸零。
// 原本是固定 3 秒無限重試，遇到「一直連不上」的狀況（見下方 onclose 的說明）會變成
// 每 3 秒打一次伺服器的迴圈——2026-08-05 查 Railway log 實際觀察到此現象。
const BASE_BACKOFF_MS = 3000;
const MAX_BACKOFF_MS = 30000;
// access token 剩餘壽命低於此值就視同已過期（涵蓋連線往返與時鐘誤差）
const TOKEN_STALE_SKEW_MS = 30000;

// 應用層心跳的間隔。
// 【為什麼一定要有心跳】TCP 斷得不乾淨時（手機切換基地台/Wi-Fi、NAT 或中間代理靜默丟棄
// 閒置連線、伺服器程序被換掉）雙方都收不到 FIN，瀏覽器的 readyState 會一直停在 OPEN。
// 這種「殭屍連線」永遠不會觸發 onclose，也就永遠走不到下面的退避重連——畫面上顯示已連線、
// 實際上再也收不到任何訊息（這正是「訊息有時候收不到」最難查的那一類）。要戳破它只有一個
// 辦法：定期送一個「要求對方回應」的封包，對方沒回就自己關掉重連。
// 【判定規則跟著 Discord Gateway】不是「送出後 N 秒沒回就殺」，而是「下一次該送心跳時、
// 上一顆還沒被 ACK 就關閉重連」。等於給了整整一個間隔的寬限，網路抖一下、App 短暫被凍住
// 都不會被誤殺，卻仍保證殭屍最多活兩個間隔。實作見下方 awaitingPong。
// 【間隔由伺服器決定】沿用 Discord Gateway 的做法：連線一建立，伺服器就在 hello 事件裡
// 把 heartbeat_interval_ms 告訴我們（後端 app/routers/websockets.py，值來自
// app_meta.get_ws_heartbeat_interval_ms()）。
// 為什麼不寫死：這個值唯一的約束在伺服器那一側——必須明顯短於路徑上中間層的閒置逾時
// （Railway / Cloudflare 之類多在 60 秒上下），而那是別人家的設定、會變，我們還是最後一個
// 知道的。寫死在 App 裡等於把一個外部會變的參數鎖在只能靠送審才改得動的地方。
// 【下面這個預設值仍然必要】hello 是連線建立後才到的，而且舊版後端根本不送：
// 收不到就沿用這個值，行為與過去完全相同。25 秒的由來同上——短於中間層逾時，又不至於
// 頻繁到一直喚醒行動網路的無線電。
const DEFAULT_HEARTBEAT_INTERVAL_MS = 25000;
// 對伺服器給的值做上下界檢查。伺服器端已經夾過一次，這裡再夾一次不是重複：
// 這是「別人叫我們每隔多久打他一次」的指令，萬一對面因為環境變數打錯（例如把單位當成秒
// 填了 25）而給出荒謬的值，代價是全體使用者的手機一起狂打——由被指使的一方自己守住底線，
// 比信任對面永遠設對更可靠。超出範圍就當作沒收到，退回預設值。
const MIN_HEARTBEAT_INTERVAL_MS = 5000;
const MAX_HEARTBEAT_INTERVAL_MS = 300000;

// 純本地解 JWT 的 exp 判斷是否過期／即將過期。解不出來一律回 false（不要因為解析失敗
// 就誤判成過期而觸發不必要的換票）。
const isJwtStale = (jwt) => {
    try {
        const payload = JSON.parse(atob(jwt.split('.')[1]));
        if (!payload || typeof payload.exp !== 'number') return false;
        return payload.exp * 1000 <= Date.now() + TOKEN_STALE_SKEW_MS;
    } catch {
        return false;
    }
};

export const WebSocketProvider = ({ children }) => {
    const { token, user } = useAuth();
    const [isConnected, setIsConnected] = useState(false);
    const [lastMessage, setLastMessage] = useState(null);
    // 「這是第幾條連線」。每次成功 open 就 +1，消費端據此得知「剛剛重連了、該補抓」——
    // 完整理由見 src/hooks/useSocketReconnect.js（WS 沒有補送機制，斷線期間的訊息永久遺失）。
    // 用遞增數字而不是布林/事件匯流排：數字是可比較的狀態，晚掛載的消費端也能拿它跟自己
    // 記下的基準比對，不會像事件那樣「沒訂閱到就永遠錯過」。
    const [connectionEpoch, setConnectionEpoch] = useState(0);
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const backoffRef = useRef(BASE_BACKOFF_MS);
    const refreshingRef = useRef(false);
    // 目前這條連線的心跳停止函式。之所以要放進 ref，是為了讓 effect cleanup 能**同步**停掉
    // 計時器，而不必等 onclose 非同步到達（那之間 interval 還會再跑，就是外洩）。
    const heartbeatStopRef = useRef(null);

    // Audio element ref for TTS playback
    const audioRef = useRef(new Audio());
    const [audioBlocked, setAudioBlocked] = useState(false);

    // 【2026-08-18 修正：只取 role，不要整個 user 物件當相依】
    // 下面的 effect 對 user 唯一的用途就是這個角色檢查，但 AuthContext 每次都會 setUser 出
    // **一顆新物件**（先用 JWT payload 湊一顆，getMe() 回來再 `{...prev, ...res.data}` 湊第二顆），
    // 內容相同、參考不同。以 user 當相依 ＝ 每次登入/換票後都白白 cleanup + 重建連線兩次。
    // 這正是正式站日誌「11:40:41.077 / .563 / 42.060 同一位使用者 1.5 秒內連三次」的來源，
    // 而每次重建都有一段無連線空窗，期間送達的訊息就此遺失（WS 不補送）。
    const userRole = user?.role;

    useEffect(() => {
        // Only connect if user is logged in (Teacher/Admin/Receptionist roles usually)
        if (!token || !userRole) {
            return;
        }

        // Allowed roles check (match backend)
        const allowedRoles = ['teacher', 'receptionist', 'admin', 'super_admin', 'parent'];
        if (!allowedRoles.includes(userRole)) {
            return;
        }

        let isMounted = true;

        // 指數退避排程重連：3s → 6s → 12s → 24s → 30s（上限），連上後由 onopen 歸零。
        const scheduleReconnect = () => {
            const delay = backoffRef.current;
            backoffRef.current = Math.min(delay * 2, MAX_BACKOFF_MS);
            reconnectTimeoutRef.current = setTimeout(connectWebSocket, delay);
        };

        const connectWebSocket = () => {
            // 【2026-08-04 修正】原本這裡自己讀 `import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'`，
            // 但這個變數在本專案「從未被設定過」（.env 只有 8 個 VITE_FIREBASE_*、vite config 也沒有 define）。
            // api.js 對這件事有完整的三層 fallback（未設 → PROD 時退 https://api.naturaledu.net），
            // 所以 HTTP API 一直是好的；WebSocket 這條卻退到 localhost:8000 → 在手機 App 上必然連不到
            // （且 Capacitor WebView origin 是 https://localhost，ws:// 還會再被 mixed-content 擋一次），
            // 造成「聊天室開著但新訊息不會即時出現」——只能靠輪詢/回前景 refetch 補救。
            // 改為直接沿用 api.js 已經算好的同一個 base（該檔本來就已被本檔 import，無新增相依）：
            // 單一真相來源，HTTP 打得到哪、WS 就連到哪，https → wss 也由此自然得出。
            const wsBase = API_BASE_URL.replace(/^http/, 'ws');

            const wsUrl = `${wsBase}/api/v1/ws/broadcast?token=${token}`;

            // Prevent multiple connections
            if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
                return;
            }

            console.log("Creating WebSocket connection to:", wsUrl);
            const ws = new WebSocket(wsUrl);

            // 心跳狀態刻意用「這條連線自己的區域變數」而不是共用 ref：舊連線的 onclose 有
            // 可能晚於新連線的 onopen 才觸發，若共用一組 ref，遲到的 onclose 會把新連線剛設好
            // 的計時器清掉，結果新連線沒有心跳。區域變數天然只影響自己這條。
            let heartbeatTimer = null;
            let awaitingPong = false;   // 已送出 ping、還沒收到對應 pong
            const stopHeartbeat = () => {
                if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
                awaitingPong = false;
            };
            // 註冊給 effect cleanup 用（ref 永遠指向最新建立的那條連線）。
            heartbeatStopRef.current = stopHeartbeat;

            // 用指定間隔（重新）啟動心跳。做成可重入的函式，是因為它有兩個呼叫點：
            // onopen 先用預設值起跳，之後 hello 到達時再用伺服器給的值重調。
            // 先 stop 再 start，確保任何情況下都只有一顆計時器在跑。
            const startHeartbeat = (intervalMs) => {
                stopHeartbeat();
                heartbeatTimer = setInterval(() => {
                    if (ws.readyState !== WebSocket.OPEN) return;

                    if (awaitingPong) {
                        // 上一輪的 ping 過了整整一個間隔還沒被回應 → 判定對端已經不在。
                        // 這裡是殭屍連線唯一的出口：readyState 還是 OPEN，但沒有人在另一頭。
                        console.warn('WS 心跳未收到 pong（已過一個完整間隔），主動關閉以觸發重連');
                        stopHeartbeat();
                        ws.close();
                        return;
                    }

                    try {
                        ws.send(JSON.stringify({ type: 'ping' }));
                        awaitingPong = true;
                    } catch (err) {
                        // send 當場丟例外代表連線已不可用，交給 onerror/onclose 那條路處理；
                        // 這裡刻意不設 awaitingPong（沒送出去的 ping 不該拿來當死亡證據）。
                        console.warn('WS ping 送出失敗：', err?.message);
                    }
                }, intervalMs);
            };

            ws.onopen = () => {
                if (!isMounted) {
                    ws.close();
                    return;
                }
                console.log("✅ WebSocket Connected");
                setIsConnected(true);
                // 通知消費端「這是新的一條連線」。刻意放在 open（而不是 close）：斷線當下沒有
                // 網路可以補抓，而且斷→連可能來回好幾次，以 open 為準每次重連只補抓一次。
                setConnectionEpoch((n) => n + 1);
                backoffRef.current = BASE_BACKOFF_MS;   // 連上就把退避歸零
                // Clear any pending reconnect
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                    reconnectTimeoutRef.current = null;
                }

                // 啟動心跳（Discord Gateway 規則：下一次該送時上一顆還沒被 ACK 就關閉重連）。
                // 逾時之後只是 close()，完全沿用既有機制：onclose → scheduleReconnect →
                // onopen → connectionEpoch +1 → useSocketReconnect 的補抓自動觸發，殭屍期間
                // 漏掉的訊息由 HTTP 補回來。刻意不另外發明一條復原路徑。
                // 先用預設值起跳，不等 hello。這樣萬一 hello 沒來（舊版後端、或那一則剛好
                // 送失敗），心跳仍然照常運作——殭屍連線的防護不該依賴一個可有可無的最佳化。
                startHeartbeat(DEFAULT_HEARTBEAT_INTERVAL_MS);
            };

            ws.onmessage = (event) => {
                if (!isMounted) return;
                try {
                    const data = JSON.parse(event.data);

                    // 心跳回應在最前面就攔掉，**絕對不能**流進 setLastMessage：lastMessage 是
                    // context value 的一部分，每 25 秒換一次就等於每 25 秒把所有消費端喚醒一次
                    // （重新 render、重跑所有依賴 lastMessage 的 effect）。也刻意不 console.log，
                    // 否則開發者工具會被心跳洗版，真正的訊息反而找不到。
                    if (data?.type === 'pong') {
                        awaitingPong = false;
                        return;
                    }

                    // 【hello】伺服器在連線建立時下發這條連線該用的心跳間隔。
                    // 與 pong 一樣必須在這裡就攔掉、不能流進 setLastMessage：它是連線的
                    // 控制訊息，不是業務訊息，放行只會平白喚醒所有消費端重新 render。
                    if (data?.type === 'hello') {
                        const serverInterval = data.heartbeat_interval_ms;
                        // 只有在拿到「合理範圍內的數字」時才改用它。非數字、超出上下界
                        // （對面環境變數設錯）一律忽略，繼續用 onopen 起跳的預設值——
                        // 寧可維持現狀，也不要照著一個明顯錯誤的指令去打伺服器。
                        if (
                            Number.isFinite(serverInterval) &&
                            serverInterval >= MIN_HEARTBEAT_INTERVAL_MS &&
                            serverInterval <= MAX_HEARTBEAT_INTERVAL_MS &&
                            serverInterval !== DEFAULT_HEARTBEAT_INTERVAL_MS
                        ) {
                            console.log(`WS 心跳間隔改用伺服器下發值：${serverInterval}ms`);
                            startHeartbeat(serverInterval);
                        }
                        return;
                    }

                    console.log("📩 WebSocket received:", data);
                    setLastMessage(data);

                    // 🔊 Auto-play Audio if available
                    if (data.type === 'broadcast' && data.audio_url) {
                        playAudio(data.audio_url);
                    }
                } catch (err) {
                    console.error("WebSocket message parse error:", err);
                }
            };

            ws.onclose = (event) => {
                // 先停心跳、再看 isMounted：連線都關了計時器還跑就是純粹的外洩，而 isMounted
                // 為 false 時下一行會直接 return，放在後面就永遠清不到。
                stopHeartbeat();
                if (!isMounted) return;
                console.log(`❌ WebSocket Disconnected. Code: ${event.code}, Reason: ${event.reason}`);
                setIsConnected(false);
                wsRef.current = null;

                // 正常關閉（1000/1001，例如換頁、App 進背景）不該自動重連——effect 的 cleanup
                // 會處理，這裡再排重連只會多開一條連線。
                if (event.code === 1000 || event.code === 1001) return;

                // 【2026-08-05 修正】原本只在 `event.code === 1008` 時才換票。但後端雖然是用
                // WS_1008_POLICY_VIOLATION 關閉（websockets.py 248/259/276/290），這個關閉發生在
                // **handshake 階段**——瀏覽器/WebView 在 handshake 未完成就被關時，一律只回報
                // **1006（abnormal closure）**，拿不到伺服器給的 1008。實測佐證：2026-08-04 家長
                // App 真機 logcat 為 `WebSocket Error` → `Code: 1006` → 3.8 秒後再次連線；
                // 2026-08-05 Railway log 則是 3 分鐘內零成功連線、JWT 驗證失敗每 3 秒一次
                // （＝下方盲目重試的間隔）。也就是說 1008 這條復原路徑實務上幾乎永遠不會進入，
                // token 一過期就落進「每 3 秒重連、永不換票」的迴圈——access token 從 90 天縮短為
                // 15 分鐘後此缺陷才頻繁暴露。（先前家長端「最後有連上」其實是其他 API 呼叫的 401
                // 攔截器順手換了票，並非 WS 自行復原。）
                //
                // 改為「不靠 close code 判斷，改看 token 自己是否過期」：本地解 JWT 的 exp 即可
                // 確定是不是憑證問題，不必猜 close code 的語意。
                const shouldRefresh = event.code === 1008 || isJwtStale(token);

                if (shouldRefresh) {
                    if (refreshingRef.current) return;   // 已有換票在跑，等新 token 觸發 effect 重連
                    refreshingRef.current = true;
                    console.log(`WS closed (code ${event.code}) with stale/rejected token — refreshing`);
                    refreshAccessToken()
                        .then(() => {
                            // 這兩個 callback 是非同步執行的：期間 effect 可能已被 cleanup（使用者
                            // 登出、token 變更、元件卸載）。此時這個 closure 捕獲的 token 與
                            // connectWebSocket 都已過時，若照排重連會用舊 token 連線、甚至在登出後
                            // 還去連——所以兩邊都必須先檢查 isMounted。
                            if (!isMounted) return;
                            // 換票成功後 authStateHandlers.onTokenRefreshed 會更新 AuthContext 的
                            // token state → 依賴 [token, userRole] 的 effect 自動 cleanup + 用新 token
                            // 重連。這裡仍排一次保險重連：萬一 token 值沒變（effect 不會重跑）也不會
                            // 卡死；若 effect 真的重跑了，cleanup 會清掉這個 timeout，且
                            // connectWebSocket 開頭的 readyState 檢查也會擋掉重複連線。
                            reconnectTimeoutRef.current = setTimeout(connectWebSocket, BASE_BACKOFF_MS);
                        })
                        .catch((err) => {
                            if (!isMounted) return;
                            // 【刻意不再從 WS 這條路 nuclear 登出】換票失敗可能只是暫時性網路問題，
                            // 而「兩個獨立的登出觸發點」正是造成家長莫名被登出的典型成因。session 是否
                            // 真的失效，交給 HTTP 401 攔截器判定——它有伺服器實際回應可依據，比
                            // WS 的 close code 可靠得多。這裡只退避重試，不動使用者的登入狀態。
                            console.warn('WS token refresh failed, will retry with backoff:', err?.message);
                            scheduleReconnect();
                        })
                        .finally(() => { refreshingRef.current = false; });
                    return;
                }

                // 其他關閉原因（網路中斷、伺服器重啟等）→ 指數退避重試，避免固定 3 秒無限打伺服器。
                scheduleReconnect();
            };

            ws.onerror = (err) => {
                if (!isMounted) return;
                console.error("WebSocket Error:", err);
                ws.close();
            };

            wsRef.current = ws;
        };

        connectWebSocket();

        return () => {
            isMounted = false;
            // 同步停掉心跳，不要等 onclose（非同步）才清：cleanup 到 onclose 之間那個 interval
            // 還會再跑，元件都卸載了計時器還活著就是外洩。
            if (heartbeatStopRef.current) {
                heartbeatStopRef.current();
                heartbeatStopRef.current = null;
            }
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
        };
    }, [token, userRole]);

    // Optimize: Auto-unlock on first user interaction
    // Optimize: Auto-unlock on first user interaction
    // DISABLED GLOBAL LISTENERS TO PREVENT LAG
    /*
    useEffect(() => {
        const handleInteraction = () => {
            // If already unblocked, do nothing (lightweight check)
            // accessing state inside event listener requires functional update or ref unless dependency
            // But we can just call enableAudio which has internal check
            enableAudio();
        };

        // Attach listeners globally
        // REMOVED 'keydown' to prevent typing lag. 'click' should be sufficient for first interaction.
        const events = ['click'];
        events.forEach(e => document.addEventListener(e, handleInteraction));

        return () => {
            events.forEach(e => document.removeEventListener(e, handleInteraction));
        };
    }, []); // Run ONCE on mount (active for lifetime until unmounted)
    */
    // Note: enableAudio handles internal state (setAudioBlocked), so repeated calls are harmless 
    // (creating a short beep is fine). 
    // If we want to be strict, we can dep on audioBlocked but simpler is better to avoid loops.

    const enableAudio = async () => {
        // Prevent repeated calls if already unlocked or audio blocked is false
        if (!audioBlocked && audioRef.current.src) return;

        try {
            // Strategy: Use Web Audio API Oscillator
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (!AudioContext) return;

            // Check if context exists, if so, resume it
            // Only create new context if strictly needed? 
            // Better strategy: Just resume the context if we have one globally, 
            // but here we just want to unlock autoplay.

            const ctx = new AudioContext();
            const oscillator = ctx.createOscillator();
            const gainNode = ctx.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(ctx.destination);

            // Near-silent beep
            gainNode.gain.value = 0.001;
            oscillator.frequency.value = 200;

            oscillator.start(0);

            setTimeout(() => {
                oscillator.stop();
                ctx.close();
            }, 100);

            // Update State
            setAudioBlocked(false);
            console.log("🔊 Audio Context Unlocked (Web Audio API)");

            // Remove listeners once successfully unlocked
            const events = ['click'];
            const handleInteraction = () => { }; // dummy for logic below? No, need actual ref.
            // We can't remove the specific listener easily unless we used a named function defined in useEffect.
            // But we can check audioBlocked check at top.

        } catch (e) {
            console.error("Audio unlock failed", e);
        }
    };

    // Audio Queue System
    const audioQueueRef = useRef([]);
    const isPlayingRef = useRef(false);

    const processQueue = async () => {
        if (isPlayingRef.current || audioQueueRef.current.length === 0) {
            return;
        }

        // Lock
        isPlayingRef.current = true;
        const nextAudio = audioQueueRef.current.shift(); // Dequeue

        try {
            console.log("🔊 Playing from queue:", nextAudio.substring(0, 30) + "...");

            // Format Source
            let src = nextAudio;
            if (!src.startsWith('http') && !src.startsWith('data:audio')) {
                src = `data:audio/mp3;base64,${nextAudio}`;
            }

            audioRef.current.src = src;
            await audioRef.current.play();
            // onended will trigger next
        } catch (err) {
            console.error("❌ Audio playback failed:", err);
            isPlayingRef.current = false;

            if (err.name === 'NotAllowedError') {
                console.warn("Autoplay blocked. User interaction required.");
                setAudioBlocked(true);
            } else {
                // If error not blocked (e.g. format error), skip to next
                processQueue();
            }
        }
    };

    // Initialize Audio Listener once
    useEffect(() => {
        const audio = audioRef.current;
        const handleEnded = () => {
            console.log("✅ Audio finished. Checking queue...");
            isPlayingRef.current = false;
            processQueue();
        };

        audio.addEventListener('ended', handleEnded);
        return () => {
            audio.removeEventListener('ended', handleEnded);
        };
    }, []);

    const playAudio = (base64Audio) => {
        // Push to queue
        audioQueueRef.current.push(base64Audio);
        console.log(`➕ Added to audio queue. Size: ${audioQueueRef.current.length}`);

        // Try to process
        processQueue();
    };

    return (
        <WebSocketContext.Provider value={{ isConnected, lastMessage, connectionEpoch, audioBlocked, enableAudio }}>
            {children}
        </WebSocketContext.Provider>
    );
};

export const useWebSocket = () => useContext(WebSocketContext);
